import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import QBankReferenceEditor from "../components/QBankReferenceEditor";
import QBankQuestionEntryModal from "../components/QBankQuestionEntryModal";
import { useAuthContext } from "../contexts/AuthContext";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import {
    getReferenceExcerpt,
    getReferencePage,
    getReferenceSource,
    normalizeReferencesForEditor,
    QBankReference,
    QBankReferenceDraft,
    serializeReferencesForSave,
} from "../utils/qbankReferences";
import styles from "./AdminQBankCollection.module.css";
import { isQBankCode, QBankCode, QBankConfig } from "../utils/qbankProfile";
import QBankQuestionImage, { QBankImage } from "../components/QBankQuestionImage";

interface Question {
    id: string;
    collection_id: string;
    question_text: string;
    options: { label: string; text: string }[];
    correct_answer: string;
    specialty: string;
    difficulty: string;
    explanation?: string;
    question_references?: QBankReference[];
    source_types_used?: string[];
    explainer_status: string;
    explainer_error?: string;
    processing_attempts?: number;
    order_index: number;
    is_published: boolean;
    created_at: string;
    updated_at: string;
    question_version_id?: string | null;
    version_number: number;
    version_status: "legacy" | "needs_review" | "approved" | "published" | "superseded";
    active_version_id?: string | null;
    published_version_id?: string | null;
    published_version_number: number;
    published_correct_answer: string;
    draft_version_id?: string | null;
    draft_version_number?: number | null;
    validation_errors?: string[];
    last_edited_by?: string | null;
    last_edited_at?: string | null;
    version_count?: number;
    question_image?: QBankImage | null;
}

interface AnswerDistributionItem {
    count: number;
    percentage: number;
}

interface VersioningState {
    distribution: {
        total: number;
        answers: Record<string, AnswerDistributionItem>;
    };
    publications: Array<{
        id: string;
        action: string;
        created_at: string;
        published_active_versions: Record<string, string | null>;
        source_publication_id?: string | null;
    }>;
}

interface QuestionVersion {
    id: string | null;
    version_number: number;
    status: string;
    correct_answer: string;
    updated_at?: string;
    is_active: boolean;
}

interface Collection {
    id: string;
    title: string;
    year: number;
    month: number;
    description?: string;
    questions_count: number;
    is_published: boolean;
}

const SDLE_SPECIALTIES = [
    "Endodontics", "Prosthodontics", "Periodontics", "Orthodontics",
    "Pediatric Dentistry", "Restorative Dentistry", "Oral Medicine", "OMFS"
];

const AdminQBankCollection: React.FC = () => {
    const { collectionId } = useParams<{ collectionId: string }>();
    const [searchParams] = useSearchParams();
    const bankParam = searchParams.get("bank") || undefined;
    const bank: QBankCode = isQBankCode(bankParam) ? bankParam : "sdle";
    const { user } = useAuthContext();

    const [collection, setCollection] = useState<Collection | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
    const [filter, setFilter] = useState<"all" | "published" | "unpublished">("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
    const [versioning, setVersioning] = useState<VersioningState>({
        distribution: { total: 0, answers: {} },
        publications: [],
    });
    const [versioningBusy, setVersioningBusy] = useState(false);
    const [historyByQuestion, setHistoryByQuestion] = useState<Record<string, QuestionVersion[]>>({});

    // Edit modal state
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
    const [editForm, setEditForm] = useState({
        question_text: "",
        options: [] as { label: string; text: string }[],
        correct_answer: "",
        specialty: "",
        difficulty: "",
        explanation: "",
        question_references: [] as QBankReferenceDraft[],
    });
    const [saving, setSaving] = useState(false);
    const [editImageFile, setEditImageFile] = useState<File | null>(null);
    const [editImageAlt, setEditImageAlt] = useState("");
    const [removeEditImage, setRemoveEditImage] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [showQuestionEntry, setShowQuestionEntry] = useState(false);
    const [qbankConfig, setQBankConfig] = useState<QBankConfig | null>(null);
    const specialties = qbankConfig?.specialties.map((specialty) => specialty.name) || SDLE_SPECIALTIES;

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
    const editImagePreview = useMemo(
        () => editImageFile ? URL.createObjectURL(editImageFile) : "",
        [editImageFile]
    );

    useEffect(() => () => {
        if (editImagePreview) URL.revokeObjectURL(editImagePreview);
    }, [editImagePreview]);

    useEffect(() => {
        if (user?.id && collectionId) {
            loadData();
        }
    }, [user, collectionId, bank]);

    const loadData = async (showLoading = true) => {
        if (!user?.id || !collectionId) return;
        if (showLoading) setLoading(true);

        try {
            const configRes = await fetch(`${apiUrl}/api/qbank/config?bank=${bank}`);
            if (configRes.ok) setQBankConfig(await configRes.json());
            // Load collection info
            const collectionsRes = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/collections?user_id=${user.id}&bank=${bank}`
            );
            if (collectionsRes.ok) {
                const collections = await collectionsRes.json();
                const col = collections.find((c: Collection) => c.id === collectionId);
                setCollection(col || null);
            }

            // Load questions
            const questionsRes = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/questions?collection_id=${collectionId}&user_id=${user.id}&bank=${bank}`
            );
            if (questionsRes.ok) {
                setQuestions(await questionsRes.json());
            }

            const versioningRes = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/collections/${collectionId}/versioning?user_id=${user.id}`
            );
            if (versioningRes.ok) {
                setVersioning(await versioningRes.json());
            }
        } catch (err) {
            console.error("Failed to load data:", err);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const toggleExpand = (questionId: string) => {
        setExpandedQuestions((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(questionId)) {
                newSet.delete(questionId);
            } else {
                newSet.add(questionId);
            }
            return newSet;
        });
    };

    const togglePublish = async (question: Question, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user?.id) return;

        const newPublishState = !question.is_published;

        // Optimistic update
        setQuestions((prev) =>
            prev.map((q) =>
                q.id === question.id ? { ...q, is_published: newPublishState } : q
            )
        );

        try {
            await authenticatedFetch(`${apiUrl}/api/admin/qbank/questions/${question.id}?bank=${bank}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    is_published: newPublishState,
                }),
            });
        } catch (err) {
            console.error("Failed to toggle publish:", err);
            // Revert on failure
            setQuestions((prev) =>
                prev.map((q) =>
                    q.id === question.id ? { ...q, is_published: question.is_published } : q
                )
            );
        }
    };

    const openEditModal = (question: Question, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingQuestion(question);
        setEditForm({
            question_text: question.question_text || "",
            options: question.options || [],
            correct_answer: question.correct_answer || "",
            specialty: question.specialty || "",
            difficulty: question.difficulty || "",
            explanation: question.explanation || "",
            question_references: normalizeReferencesForEditor(question.question_references),
        });
        setEditImageFile(null);
        setEditImageAlt(question.question_image?.alt_text || "");
        setRemoveEditImage(false);
    };

    const closeEditModal = () => {
        setEditingQuestion(null);
        setEditImageFile(null);
        setEditImageAlt("");
        setRemoveEditImage(false);
    };

    const moveCorrectAnswer = (targetIndex: number) => {
        const correctIndex = editForm.options.findIndex(
            (option) => option.label === editForm.correct_answer
        );
        if (correctIndex < 0 || targetIndex < 0 || targetIndex >= editForm.options.length) return;
        const reordered = [...editForm.options];
        const [correctOption] = reordered.splice(correctIndex, 1);
        reordered.splice(targetIndex, 0, correctOption);
        const relabeled = reordered.map((option, index) => ({
            ...option,
            label: String.fromCharCode(65 + index),
        }));
        setEditForm({
            ...editForm,
            options: relabeled,
            correct_answer: String.fromCharCode(65 + targetIndex),
        });
    };

    const addOption = () => {
        const labels = "ABCDEFGHIJ";
        const nextLabel = labels[editForm.options.length] || String(editForm.options.length + 1);
        setEditForm({
            ...editForm,
            options: [...editForm.options, { label: nextLabel, text: "" }],
        });
    };

    const removeOption = (idx: number) => {
        const labels = "ABCDEFGHIJ";
        const originalCorrectIdx = editForm.options.findIndex(o => o.label === editForm.correct_answer);
        const newOptions = editForm.options
            .filter((_, i) => i !== idx)
            .map((opt, i) => ({ ...opt, label: labels[i] || String(i + 1) }));
        // Recalculate correct answer based on relabeling
        let updatedCorrect = "";
        if (originalCorrectIdx !== -1 && originalCorrectIdx !== idx) {
            const newIdx = originalCorrectIdx > idx ? originalCorrectIdx - 1 : originalCorrectIdx;
            updatedCorrect = newOptions[newIdx]?.label || "";
        }
        setEditForm({
            ...editForm,
            options: newOptions,
            correct_answer: updatedCorrect,
        });
    };

    const buildQuestionUpdatePayload = () => ({
        question_text: editForm.question_text,
        options: editForm.options,
        correct_answer: editForm.correct_answer,
        specialty: editForm.specialty,
        difficulty: editForm.difficulty,
        explanation: editForm.explanation,
        question_references: serializeReferencesForSave(editForm.question_references),
    });

    const saveQuestion = async () => {
        if (!editingQuestion || !user?.id) return;
        setSaving(true);

        try {
            const questionUpdates = buildQuestionUpdatePayload();
            const imageChanged = Boolean(
                editImageFile
                || removeEditImage
                || (editingQuestion.question_image && editImageAlt.trim() !== editingQuestion.question_image.alt_text)
            );
            let requestInit: RequestInit;
            if (imageChanged) {
                const formData = new FormData();
                formData.append("payload", JSON.stringify({ user_id: user.id, ...questionUpdates }));
                if (editImageFile) formData.append("image", editImageFile);
                if (editImageFile || editingQuestion.question_image) {
                    formData.append("image_alt_text", editImageAlt.trim());
                }
                if (removeEditImage) formData.append("remove_image", "true");
                requestInit = { method: "PUT", body: formData };
            } else {
                requestInit = {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id, ...questionUpdates }),
                };
            }
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/questions/${editingQuestion.id}?bank=${bank}`,
                requestInit
            );

            if (res.ok) {
                closeEditModal();
                await loadData();
                (window as any).showNotification?.("Draft version saved for review", "success");
            } else {
                const error = await res.json().catch(() => ({}));
                throw new Error(error.error || "Failed to save draft");
            }
        } catch (err: any) {
            console.error("Failed to save question:", err);
            (window as any).showNotification?.(err.message || "Failed to save draft", "error");
        } finally {
            setSaving(false);
        }
    };

    const deleteQuestion = async (questionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user?.id) return;
        if (!window.confirm("Are you sure you want to delete this question? This cannot be undone.")) return;

        try {
            await authenticatedFetch(`${apiUrl}/api/admin/qbank/questions/${questionId}?bank=${bank}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            setQuestions((prev) => prev.filter((q) => q.id !== questionId));
        } catch (err) {
            console.error("Failed to delete question:", err);
        }
    };

    const retryQuestion = async (questionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user?.id) return;

        try {
            await authenticatedFetch(`${apiUrl}/api/admin/qbank/questions/${questionId}/retry?bank=${bank}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            loadData();
        } catch (err) {
            console.error("Failed to retry question:", err);
        }
    };

    const copyId = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(id);
        (window as any).showNotification?.("ID copied to clipboard", "success");
    };

    const selectedIds = () => selectedQuestions.size > 0
        ? Array.from(selectedQuestions)
        : null;

    const runVersionAction = async (
        path: string,
        successMessage: string,
        body: Record<string, unknown> = {},
        method: "POST" | "DELETE" = "POST",
    ) => {
        if (!user?.id) return false;
        setVersioningBusy(true);
        try {
            const response = await authenticatedFetch(`${apiUrl}${path}`, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id, ...body }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || "Version operation failed");
            await loadData();
            (window as any).showNotification?.(successMessage, "success");
            return true;
        } catch (err: any) {
            (window as any).showNotification?.(err.message || "Version operation failed", "error");
            return false;
        } finally {
            setVersioningBusy(false);
        }
    };

    const balanceQuestions = async () => {
        if (!collectionId) return;
        const scope = selectedQuestions.size
            ? `${selectedQuestions.size} selected questions`
            : "every published question in this collection";
        if (!window.confirm(`Create balanced draft versions for ${scope}? Nothing will be published yet.`)) return;
        await runVersionAction(
            `/api/admin/qbank/collections/${collectionId}/balance`,
            "Balanced drafts created. Review each question before approval.",
            { question_ids: selectedIds() },
        );
    };

    const approveQuestions = async () => {
        if (!collectionId) return;
        await runVersionAction(
            `/api/admin/qbank/collections/${collectionId}/approve-versions`,
            "Validation-clean drafts approved",
            { question_ids: selectedIds() },
        );
    };

    const publishQuestions = async () => {
        if (!collectionId) return;
        const scope = selectedQuestions.size ? "the selected approved drafts" : "all approved drafts";
        if (!window.confirm(`Publish ${scope}? New QBank views and new mocks will use them immediately.`)) return;
        if (await runVersionAction(
            `/api/admin/qbank/collections/${collectionId}/publish-versions`,
            "Question versions published",
            { question_ids: selectedIds() },
        )) {
            setSelectedQuestions(new Set());
        }
    };

    const approveQuestion = async (question: Question) => {
        if (!question.draft_version_id) return;
        await runVersionAction(
            `/api/admin/qbank/questions/${question.id}/draft/approve`,
            `v${question.draft_version_number} approved`,
            { version_id: question.draft_version_id },
        );
    };

    const publishQuestion = async (question: Question) => {
        if (!question.draft_version_id) return;
        if (!window.confirm(`Publish v${question.draft_version_number} for this question?`)) return;
        await runVersionAction(
            `/api/admin/qbank/questions/${question.id}/publish-version`,
            `v${question.draft_version_number} published`,
            { version_id: question.draft_version_id },
        );
    };

    const discardDraft = async (question: Question) => {
        if (!window.confirm(`Discard draft v${question.draft_version_number}?`)) return;
        await runVersionAction(
            `/api/admin/qbank/questions/${question.id}/draft`,
            "Draft discarded",
            {},
            "DELETE",
        );
    };

    const loadVersionHistory = async (questionId: string) => {
        if (!user?.id) return;
        if (historyByQuestion[questionId]) {
            setHistoryByQuestion((previous) => {
                const next = { ...previous };
                delete next[questionId];
                return next;
            });
            return;
        }
        const response = await authenticatedFetch(
            `${apiUrl}/api/admin/qbank/questions/${questionId}/versions?user_id=${user.id}`
        );
        if (response.ok) {
            const history = await response.json();
            setHistoryByQuestion((previous) => ({
                ...previous,
                [questionId]: history,
            }));
        }
    };

    const rollbackQuestion = async (question: Question, target: QuestionVersion) => {
        if (!window.confirm(`Roll this question back to v${target.version_number}?`)) return;
        await runVersionAction(
            `/api/admin/qbank/questions/${question.id}/rollback-version`,
            `Question rolled back to v${target.version_number}`,
            { target_version_id: target.id },
        );
        setHistoryByQuestion((previous) => {
            const next = { ...previous };
            delete next[question.id];
            return next;
        });
    };

    const rolledBackPublicationIds = new Set(
        versioning.publications
            .filter((publication) => publication.action === "collection_rollback")
            .map((publication) => publication.source_publication_id)
    );
    const latestCollectionPublication = versioning.publications.find(
        (publication) => publication.action === "collection_publish" && !rolledBackPublicationIds.has(publication.id)
    );

    const rollbackCollection = async () => {
        if (!collectionId || !latestCollectionPublication) return;
        if (!window.confirm("Roll back the latest collection publication? Later question changes will prevent an unsafe rollback.")) return;
        await runVersionAction(
            `/api/admin/qbank/collections/${collectionId}/publications/${latestCollectionPublication.id}/rollback`,
            "Collection publication rolled back",
        );
    };

    const toggleSelected = (questionId: string) => {
        setSelectedQuestions((previous) => {
            const next = new Set(previous);
            if (next.has(questionId)) next.delete(questionId);
            else next.add(questionId);
            return next;
        });
    };

    const getStats = () => {
        return {
            total: questions.length,
            published: questions.filter((q) => q.is_published).length,
            unpublished: questions.filter((q) => !q.is_published).length,
            completed: questions.filter((q) => q.explainer_status === "completed").length,
            processing: questions.filter((q) => q.explainer_status === "processing").length,
            pending: questions.filter((q) => q.explainer_status === "pending").length,
            failed: questions.filter((q) => q.explainer_status === "failed").length,
            versionDrafts: questions.filter((q) => Boolean(q.draft_version_id)).length,
            needsReviewVersions: questions.filter((q) => q.version_status === "needs_review").length,
            approvedVersions: questions.filter((q) => q.version_status === "approved").length,
        };
    };

    const getStatusColor = (status: string): string => {
        const colors: Record<string, string> = {
            pending: "#f59e0b",
            processing: "#7b5cff",
            completed: "#22c55e",
            failed: "#ef4444",
        };
        return colors[status] || "#888";
    };

    const publishAllUnpublished = async () => {
        if (!user?.id) return;

        const unpublishedReady = questions.filter(
            (q) => q.explainer_status === "completed" && !q.is_published
        );

        if (unpublishedReady.length === 0) {
            alert("No unpublished ready questions to publish.");
            return;
        }

        if (!window.confirm(
            `Publish ${unpublishedReady.length} unpublished questions?`
        )) return;

        // Optimistic update
        setQuestions((prev) =>
            prev.map((q) =>
                q.explainer_status === "completed" && !q.is_published
                    ? { ...q, is_published: true }
                    : q
            )
        );

        // Update each question
        try {
            await Promise.all(
                unpublishedReady.map((q) =>
                    authenticatedFetch(`${apiUrl}/api/admin/qbank/questions/${q.id}?bank=${bank}`, {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            user_id: user.id,
                            is_published: true,
                        }),
                    })
                )
            );
        } catch (err) {
            console.error("Failed to publish all:", err);
            loadData(); // Reload to get correct state
        }
    };

    const exportPdf = async () => {
        if (!user?.id || !collectionId) return;

        // Check if there are published questions
        const publishedCount = questions.filter((q) => q.is_published).length;
        if (publishedCount === 0) {
            (window as any).showNotification?.("No published questions to export", "warning");
            return;
        }

        setExporting(true);

        try {
            const response = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/collections/${collectionId}/export-pdf?user_id=${user.id}&bank=${bank}`
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Failed to generate PDF");
            }

            // Get the filename from the Content-Disposition header if available
            const contentDisposition = response.headers.get("Content-Disposition");
            let filename = `${bank.toUpperCase()}_QBank_${collection?.title || "Collection"}.pdf`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) {
                    filename = match[1];
                }
            }

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            (window as any).showNotification?.(`PDF exported successfully (${publishedCount} questions)`, "success");
        } catch (err: any) {
            console.error("Failed to export PDF:", err);
            (window as any).showNotification?.(err.message || "Failed to export PDF", "error");
        } finally {
            setExporting(false);
        }
    };

    const filteredQuestions = questions.filter((q) => {
        // Apply publish filter
        if (filter === "published" && !q.is_published) return false;
        if (filter === "unpublished" && q.is_published) return false;

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            const questionText = (q.question_text || "").toLowerCase();
            const explanation = (q.explanation || "").toLowerCase();
            const optionsText = (q.options || []).map(o => o.text.toLowerCase()).join(" ");
            const specialty = (q.specialty || "").toLowerCase();

            return (
                questionText.includes(query) ||
                explanation.includes(query) ||
                optionsText.includes(query) ||
                specialty.includes(query)
            );
        }

        return true;
    });

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner} />
                <p>Loading collection...</p>
            </div>
        );
    }

    if (!collection) {
        return (
            <div className={styles.container}>
                <p className={styles.emptyState}>Collection not found.</p>
                <Link to={`/admin/qbank?bank=${bank}`} className={styles.backLink}>
                    ← Back to QBank
                </Link>
            </div>
        );
    }

    const stats = getStats();

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <Link to={`/admin/qbank?bank=${bank}`} className={styles.backLink}>
                        ← Back to QBank
                    </Link>
                    <div className={styles.titleRow}>
                        <h1 className={styles.headerTitle}>{collection.title}</h1>
                        <span className={`${styles.publishBadge} ${collection.is_published ? styles.published : styles.draft}`}>
                            {collection.is_published ? "Published" : "Draft"}
                        </span>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.primaryButton} onClick={() => setShowQuestionEntry(true)}>
                        + Add Questions
                    </button>
                    <button
                        className={styles.exportButton}
                        onClick={exportPdf}
                        disabled={exporting || stats.published === 0}
                        title={stats.published === 0 ? "No published questions to export" : "Export published questions to PDF"}
                    >
                        {exporting ? (
                            <>
                                <span className={styles.exportSpinner} />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="12" y1="18" x2="12" y2="12" />
                                    <line x1="9" y1="15" x2="12" y2="18" />
                                    <line x1="15" y1="15" x2="12" y2="18" />
                                </svg>
                                Export PDF
                            </>
                        )}
                    </button>
                    <button className={styles.secondaryButton} onClick={() => loadData()}>
                        ↻ Refresh
                    </button>
                </div>
            </div>

            <section className={styles.versionControlPanel}>
                <div className={styles.versionControlHeader}>
                    <div>
                        <h2>Published answer distribution</h2>
                        <p>Only the versions currently visible to learners are counted.</p>
                    </div>
                    <div className={styles.versionControlActions}>
                        <button className={styles.secondaryButton} onClick={balanceQuestions} disabled={versioningBusy}>
                            Balance {selectedQuestions.size ? `Selected (${selectedQuestions.size})` : "Collection"}
                        </button>
                        <button className={styles.secondaryButton} onClick={approveQuestions} disabled={versioningBusy || stats.needsReviewVersions === 0}>
                            Approve {selectedQuestions.size ? "Selected" : "Reviewable Drafts"}
                        </button>
                        <button className={styles.primaryButton} onClick={publishQuestions} disabled={versioningBusy || stats.approvedVersions === 0}>
                            Publish {selectedQuestions.size ? "Selected" : "Approved Drafts"}
                        </button>
                        {latestCollectionPublication && (
                            <button className={styles.dangerButton} onClick={rollbackCollection} disabled={versioningBusy}>
                                Roll Back Last Collection Publish
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles.distributionGrid}>
                    {(["A", "B", "C", "D"] as const).map((label) => {
                        const item = versioning.distribution.answers[label] || { count: 0, percentage: 0 };
                        return (
                            <div className={styles.distributionItem} key={label}>
                                <span className={styles.distributionLabel}>{label}</span>
                                <strong>{item.percentage}%</strong>
                                <small>{item.count} questions</small>
                                <div className={styles.distributionTrack}>
                                    <span style={{ width: `${Math.min(100, item.percentage)}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Stats */}
            <div className={styles.statsBar}>
                <div className={styles.statItem}>
                    <span className={styles.statValue}>{stats.total}</span>
                    <span className={styles.statLabel}>Total</span>
                </div>
                <div className={styles.statItem}>
                    <span className={styles.statValue}>{stats.versionDrafts}</span>
                    <span className={styles.statLabel}>Version Drafts</span>
                </div>
                <div className={`${styles.statItem} ${styles.statCompleted}`}>
                    <span className={styles.statValue}>{stats.completed}</span>
                    <span className={styles.statLabel}>Ready</span>
                </div>
                <div className={styles.statItem}>
                    <span className={styles.statValue}>{stats.published}</span>
                    <span className={styles.statLabel}>Published</span>
                </div>
                {stats.processing > 0 && (
                    <div className={`${styles.statItem} ${styles.statProcessing}`}>
                        <span className={styles.statValue}>{stats.processing}</span>
                        <span className={styles.statLabel}>Processing</span>
                    </div>
                )}
                {stats.pending > 0 && (
                    <div className={`${styles.statItem} ${styles.statPending}`}>
                        <span className={styles.statValue}>{stats.pending}</span>
                        <span className={styles.statLabel}>Pending</span>
                    </div>
                )}
                {stats.failed > 0 && (
                    <div className={`${styles.statItem} ${styles.statFailed}`}>
                        <span className={styles.statValue}>{stats.failed}</span>
                        <span className={styles.statLabel}>Failed</span>
                    </div>
                )}

                {/* Publish All Button */}
                {stats.completed > stats.published && (
                    <button
                        className={styles.publishAllButton}
                        onClick={publishAllUnpublished}
                    >
                        Make All Ready Visible ({stats.completed - stats.published})
                    </button>
                )}
            </div>

            {/* Controls */}
            <div className={styles.controlsBar}>
                <div className={styles.searchGroup}>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search questions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            className={styles.clearSearch}
                            onClick={() => setSearchQuery("")}
                            title="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>
                <div className={styles.filterGroup}>
                    <span className={styles.filterLabel}>Filter:</span>
                    <select
                        className={styles.select}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value as any)}
                    >
                        <option value="all">All Questions ({stats.total})</option>
                        <option value="published">Published ({stats.published})</option>
                        <option value="unpublished">Unpublished ({stats.unpublished})</option>
                    </select>
                </div>
                <button
                    className={styles.linkButton}
                    onClick={() => setExpandedQuestions(new Set(filteredQuestions.map((q) => q.id)))}
                >
                    Expand All
                </button>
                <button
                    className={styles.linkButton}
                    onClick={() => setExpandedQuestions(new Set())}
                >
                    Collapse All
                </button>
            </div>

            {/* Question List */}
            {searchQuery && (
                <div className={styles.searchResultsInfo}>
                    Found {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} matching "{searchQuery}"
                </div>
            )}
            <div className={styles.questionList}>
                {filteredQuestions.length === 0 ? (
                    <p className={styles.emptyState}>
                        {searchQuery
                            ? `No questions found matching "${searchQuery}"`
                            : "No questions match the current filter."}
                    </p>
                ) : (
                    filteredQuestions.map((q, idx) => {
                        const isExpanded = expandedQuestions.has(q.id);
                        const displayText = q.question_text && q.question_text.length > 80
                            ? `${q.question_text.substring(0, 80)}...`
                            : q.question_text || "(No text)";

                        return (
                            <div
                                key={q.id}
                                className={`${styles.questionCard} ${isExpanded ? styles.expanded : ""}`}
                            >
                                {/* Header */}
                                <div
                                    className={styles.questionHeader}
                                    onClick={() => toggleExpand(q.id)}
                                >
                                    <label
                                        className={styles.questionSelector}
                                        onClick={(event) => event.stopPropagation()}
                                        title="Select this question for a collection action"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedQuestions.has(q.id)}
                                            onChange={() => toggleSelected(q.id)}
                                        />
                                    </label>
                                    <div className={styles.questionInfo}>
                                        <div className={styles.questionIdRow}>
                                            <span className={styles.questionNumber}>Q{idx + 1}</span>
                                            <span
                                                className={styles.questionId}
                                                onClick={(e) => copyId(q.id, e)}
                                                title="Click to copy ID"
                                            >
                                                {q.id.substring(0, 8)}...
                                            </span>
                                            <span className={`${styles.versionBadge} ${styles[`version_${q.version_status}`] || ""}`}>
                                                v{q.version_number} · {q.version_status.replace("_", " ")}
                                            </span>
                                        </div>
                                        <span className={styles.questionPreview}>{displayText}</span>
                                    </div>

                                    <div className={styles.questionMeta}>
                                        <span
                                            className={styles.statusBadge}
                                            style={{ background: getStatusColor(q.explainer_status) }}
                                        >
                                            {q.explainer_status}
                                        </span>

                                        {q.explainer_status === "completed" && (
                                            <div className={styles.publishToggle} onClick={(e) => e.stopPropagation()}>
                                                <span className={styles.toggleLabel}>
                                                    {q.is_published ? "Published" : "Draft"}
                                                </span>
                                                <div
                                                    className={`${styles.toggleSwitch} ${q.is_published ? styles.active : ""}`}
                                                    onClick={(e) => togglePublish(q, e)}
                                                />
                                            </div>
                                        )}

                                        <span className={styles.expandIcon}>
                                            {isExpanded ? "−" : "+"}
                                        </span>
                                    </div>
                                </div>

                                {/* Expanded Content */}
                                {isExpanded && (
                                    <div className={styles.questionContent}>
                                        <div className={styles.tagRow}>
                                            <span className={styles.tag}>{q.specialty || "Unknown"}</span>
                                            <span className={styles.tag}>{q.difficulty || "Unknown"}</span>
                                            <span className={styles.tag}>Order: {q.order_index}</span>
                                            <span className={styles.tag}>Correct: {q.correct_answer}</span>
                                            <span className={styles.tag}>Learners: v{q.published_version_number}</span>
                                        </div>

                                        <div className={styles.versionSummary}>
                                            <strong>Question version v{q.version_number}</strong>
                                            <span>Status: {q.version_status.replace("_", " ")}</span>
                                            {q.last_edited_at && (
                                                <span>Edited {new Date(q.last_edited_at).toLocaleString()}</span>
                                            )}
                                            {q.validation_errors && q.validation_errors.length > 0 && (
                                                <span className={styles.validationError}>
                                                    Review required: {q.validation_errors.join("; ")}
                                                </span>
                                            )}
                                        </div>

                                        <p className={styles.fullQuestionText}>
                                            {q.question_text || "No question text"}
                                        </p>
                                        <QBankQuestionImage image={q.question_image} compact />

                                        <div className={styles.optionsList}>
                                            {(q.options || []).map((opt) => (
                                                <div
                                                    key={opt.label}
                                                    className={`${styles.option} ${q.correct_answer === opt.label && q.explainer_status === "completed"
                                                        ? styles.correct
                                                        : ""
                                                        }`}
                                                >
                                                    <span className={styles.optionLabel}>{opt.label}</span>
                                                    <span className={styles.optionText}>{opt.text}</span>
                                                    {q.correct_answer === opt.label && q.explainer_status === "completed" && (
                                                        <span className={styles.correctBadge}>Correct</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        {q.explainer_status === "completed" && q.explanation && (
                                            <div className={styles.explanationBox}>
                                                <h4>Explanation</h4>
                                                <div className={styles.explanationContent}>
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                        {q.explanation}
                                                    </ReactMarkdown>
                                                </div>
                                            </div>
                                        )}

                                        {q.question_references && q.question_references.length > 0 && (
                                            <div className={styles.referencesBox}>
                                                <h4>References</h4>
                                                {q.question_references.map((ref: any, i: number) => {
                                                    const excerpt = getReferenceExcerpt(ref);
                                                    const page = getReferencePage(ref);
                                                    const source = getReferenceSource(ref);
                                                    return (
                                                        <div key={i} className={styles.referenceItem}>
                                                            {excerpt && (
                                                                <div className={styles.referenceExcerpt}>
                                                                    "{excerpt}"
                                                                </div>
                                                            )}
                                                            <div className={styles.referenceSource}>
                                                                — {source}
                                                                {page && <span>, p. {page}</span>}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {q.explainer_status === "failed" && (
                                            <div className={styles.errorBox}>
                                                <p>
                                                    <strong>Error:</strong> {q.explainer_error || "Unknown error"}
                                                </p>
                                            </div>
                                        )}

                                        {q.explainer_status === "pending" && (
                                            <p className={`${styles.statusNote} ${styles.pending}`}>
                                                Waiting for processing...
                                            </p>
                                        )}

                                        {q.explainer_status === "processing" && (
                                            <p className={`${styles.statusNote} ${styles.processing}`}>
                                                Agent6 is generating explanation...
                                            </p>
                                        )}

                                        {/* Action Buttons */}
                                        <div className={styles.actionButtons}>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={(e) => openEditModal(q, e)}
                                            >
                                                Edit
                                            </button>
                                            {q.draft_version_id && q.version_status === "needs_review" && (
                                                <button
                                                    className={styles.secondaryButton}
                                                    onClick={() => approveQuestion(q)}
                                                    disabled={versioningBusy || Boolean(q.validation_errors?.length)}
                                                >
                                                    Approve v{q.draft_version_number}
                                                </button>
                                            )}
                                            {q.draft_version_id && q.version_status === "approved" && (
                                                <button
                                                    className={styles.primaryButton}
                                                    onClick={() => publishQuestion(q)}
                                                    disabled={versioningBusy}
                                                >
                                                    Publish v{q.draft_version_number}
                                                </button>
                                            )}
                                            {q.draft_version_id && (
                                                <button
                                                    className={styles.secondaryButton}
                                                    onClick={() => discardDraft(q)}
                                                    disabled={versioningBusy}
                                                >
                                                    Discard Draft
                                                </button>
                                            )}
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() => loadVersionHistory(q.id)}
                                                disabled={versioningBusy}
                                            >
                                                {historyByQuestion[q.id] ? "Hide History" : `History (${q.version_count || 1})`}
                                            </button>
                                            {q.explainer_status === "failed" && (
                                                <button
                                                    className={styles.primaryButton}
                                                    onClick={(e) => retryQuestion(q.id, e)}
                                                >
                                                    Retry
                                                </button>
                                            )}
                                            {!q.is_published && q.version_count === 1 && (
                                                <button
                                                    className={styles.dangerButton}
                                                    onClick={(e) => deleteQuestion(q.id, e)}
                                                >
                                                    Delete Unused Question
                                                </button>
                                            )}
                                        </div>

                                        {historyByQuestion[q.id] && (
                                            <div className={styles.versionHistory}>
                                                <h4>Version history</h4>
                                                {historyByQuestion[q.id].map((version) => (
                                                    <div className={styles.versionHistoryRow} key={version.id || "legacy"}>
                                                        <div>
                                                            <strong>v{version.version_number}</strong>
                                                            <span>{version.status.replace("_", " ")}</span>
                                                            <span>Correct: {version.correct_answer}</span>
                                                            {version.updated_at && <span>{new Date(version.updated_at).toLocaleString()}</span>}
                                                        </div>
                                                        {!version.is_active && (
                                                            <button
                                                                className={styles.secondaryButton}
                                                                onClick={() => rollbackQuestion(q, version)}
                                                                disabled={versioningBusy}
                                                            >
                                                                Roll Back Here
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {showQuestionEntry && user?.id && (
                <QBankQuestionEntryModal
                    userId={user.id}
                    bankCode={bank}
                    specialties={specialties}
                    defaultSpecialty={qbankConfig?.default_specialty || specialties[0]}
                    optionCount={qbankConfig?.option_count ?? null}
                    collections={[collection]}
                    initialCollectionId={collection.id}
                    lockCollection
                    onClose={() => setShowQuestionEntry(false)}
                    onImported={async () => {
                        await loadData(false);
                    }}
                />
            )}

            {/* Edit Modal */}
            {editingQuestion && (
                <div className={styles.modal} onClick={closeEditModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Edit Question</h2>
                            <button className={styles.modalClose} onClick={closeEditModal} title="Close">
                                ×
                            </button>
                        </div>

                        {/* Question Text */}
                        <div className={styles.formSection}>
                            <div className={styles.formRow}>
                                <label>Question Text</label>
                                <textarea
                                    className={styles.textarea}
                                    value={editForm.question_text}
                                    onChange={(e) => setEditForm({ ...editForm, question_text: e.target.value })}
                                    rows={3}
                                />
                            </div>
                        </div>

                        {/* Optional versioned image */}
                        <div className={styles.formSection}>
                            <div className={styles.formRow}>
                                <label>Question Image</label>
                                {!removeEditImage && (editImagePreview || editingQuestion.question_image?.url) && (
                                    <img
                                        className={styles.questionImagePreview}
                                        src={editImagePreview || editingQuestion.question_image?.url}
                                        alt="Selected question preview"
                                    />
                                )}
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0] || null;
                                        setEditImageFile(file);
                                        if (file) setRemoveEditImage(false);
                                    }}
                                />
                                {(editImageFile || (editingQuestion.question_image && !removeEditImage)) && (
                                    <input
                                        type="text"
                                        className={styles.optionEditInput}
                                        value={editImageAlt}
                                        maxLength={300}
                                        onChange={(event) => setEditImageAlt(event.target.value)}
                                        placeholder="Objectively describe the image without revealing the answer"
                                    />
                                )}
                                {(editImageFile || editingQuestion.question_image) && !removeEditImage && (
                                    <button
                                        type="button"
                                        className={styles.removeOptionBtn}
                                        onClick={() => {
                                            setEditImageFile(null);
                                            setRemoveEditImage(Boolean(editingQuestion.question_image));
                                            if (!editingQuestion.question_image) setEditImageAlt("");
                                        }}
                                    >
                                        Remove image
                                    </button>
                                )}
                                {removeEditImage && (
                                    <button
                                        type="button"
                                        className={styles.secondaryButton}
                                        onClick={() => setRemoveEditImage(false)}
                                    >
                                        Keep current image
                                    </button>
                                )}
                                <span className={styles.formHint}>
                                    JPEG, PNG, or WebP up to 10 MB. Image questions require a completed answer and explanation.
                                </span>
                            </div>
                        </div>

                        {/* Options */}
                        <div className={styles.formSection}>
                            <div className={styles.formRow}>
                                <div className={styles.formLabelRow}>
                                    <label>Options</label>
                                    <span className={styles.formHint}>Edit the content or deliberately select the correct answer.</span>
                                </div>
                                <div className={styles.correctPositionControl}>
                                    <span>Move the current correct answer to:</span>
                                    {editForm.options.map((option, index) => (
                                        <button
                                            key={option.label}
                                            type="button"
                                            className={editForm.correct_answer === option.label ? styles.correctPositionActive : ""}
                                            onClick={() => moveCorrectAnswer(index)}
                                        >
                                            {String.fromCharCode(65 + index)}
                                        </button>
                                    ))}
                                </div>
                                {editForm.options.map((opt, idx) => (
                                    <div key={idx} className={styles.optionEditRow}>
                                        <input
                                            type="radio"
                                            className={styles.correctRadio}
                                            name="correct_answer"
                                            checked={editForm.correct_answer === opt.label}
                                            onChange={() => setEditForm({ ...editForm, correct_answer: opt.label })}
                                            title={`Mark ${opt.label} as correct`}
                                        />
                                        <span className={`${styles.optionEditLabel} ${editForm.correct_answer === opt.label ? styles.optionEditLabelCorrect : ""}`}>
                                            {opt.label}
                                        </span>
                                        <input
                                            type="text"
                                            className={`${styles.optionEditInput} ${editForm.correct_answer === opt.label ? styles.optionEditInputCorrect : ""}`}
                                            value={opt.text}
                                            onChange={(e) => {
                                                const newOptions = [...editForm.options];
                                                newOptions[idx] = { ...opt, text: e.target.value };
                                                setEditForm({ ...editForm, options: newOptions });
                                            }}
                                            placeholder={`Option ${opt.label}`}
                                        />
                                {bank !== "smle" && editForm.options.length > 2 && (
                                            <button
                                                type="button"
                                                className={styles.removeOptionBtn}
                                                onClick={() => removeOption(idx)}
                                                title="Remove option"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {bank !== "smle" && editForm.options.length < 6 && (
                                    <button
                                        type="button"
                                        className={styles.addOptionBtn}
                                        onClick={addOption}
                                    >
                                        + Add Option
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Metadata */}
                        <div className={styles.formSection}>
                            <div className={styles.formRowInline}>
                                <div className={styles.formRow}>
                                    <label>Specialty</label>
                                    <select
                                        className={styles.select}
                                        value={editForm.specialty}
                                        onChange={(e) => setEditForm({ ...editForm, specialty: e.target.value })}
                                    >
                                        {specialties.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.formRow}>
                                    <label>Difficulty</label>
                                    <select
                                        className={styles.select}
                                        value={editForm.difficulty}
                                        onChange={(e) => setEditForm({ ...editForm, difficulty: e.target.value })}
                                    >
                                        <option value="easy">Easy</option>
                                        <option value="medium">Medium</option>
                                        <option value="hard">Hard</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Explanation */}
                        <div className={styles.formSection}>
                            <div className={styles.formRow}>
                                <label>Explanation</label>
                                <textarea
                                    className={styles.textarea}
                                    value={editForm.explanation}
                                    onChange={(e) => setEditForm({ ...editForm, explanation: e.target.value })}
                                    rows={5}
                                    placeholder="Markdown supported"
                                />
                            </div>
                        </div>

                        {/* References */}
                        <div className={styles.formSection}>
                            <div className={styles.formRow}>
                                <label>References</label>
                                <QBankReferenceEditor
                                    references={editForm.question_references}
                                    onChange={(question_references) => setEditForm({
                                        ...editForm,
                                        question_references,
                                    })}
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className={styles.modalActions}>
                            <button className={styles.secondaryButton} onClick={closeEditModal}>
                                Cancel
                            </button>
                            <div className={styles.modalActionsRight}>
                                <button
                                    className={styles.primaryButton}
                                    onClick={saveQuestion}
                                    disabled={saving || Boolean(
                                        !removeEditImage
                                        && (editImageFile || editingQuestion.question_image)
                                        && (!editImageAlt.trim() || !editForm.correct_answer || !editForm.explanation.trim())
                                    )}
                                >
                                    {saving ? "Saving..." : "Save as Draft Version"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminQBankCollection;
